/**
 * BankDetailsForm — reusable component used in:
 *   - User Profile page
 *   - Partner Profile page
 *   - Admin Dashboard
 *
 * Props:
 *   token      — JWT auth token
 *   onSaved    — callback after successful save
 *   compact    — render in compact/inline mode
 *   required   — show "required before booking/withdrawal" banner
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { API_URL as API } from '../config/api';

export default function BankDetailsForm({ token, onSaved, compact = false, required = false }) {
  const [existing, setExisting] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    accountHolderName: '',
    upiId: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    accountType: 'savings',
  });

  useEffect(() => {
    fetchBankDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/bank-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.hasBankDetails) {
        setExisting(res.data.bankDetails);
        setForm({
          accountHolderName: res.data.bankDetails.accountHolderName || '',
          upiId: res.data.bankDetails.upiId || '',
          accountNumber: res.data.bankDetails.accountNumber || '',
          ifscCode: res.data.bankDetails.ifscCode || '',
          bankName: res.data.bankDetails.bankName || '',
          accountType: res.data.bankDetails.accountType || 'savings',
        });
      } else {
        setEditing(true); // auto-open form if no details
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.accountHolderName.trim()) return setError('Account holder name is required.');
    
    const hasUpi = !!(form.upiId && form.upiId.trim());
    const hasBank = !!(form.accountNumber && form.accountNumber.trim());

    if (!hasUpi && !hasBank) {
      return setError('Either a UPI ID or Bank Account Details must be provided.');
    }

    if (hasUpi) {
      const cleanUpi = form.upiId.trim();
      if (!cleanUpi.includes('@') || cleanUpi.length < 3) {
        return setError('Please enter a valid UPI ID (e.g. name@bank).');
      }
    }

    if (hasBank) {
      const cleanAcc = form.accountNumber.trim();
      if (!/^\d{9,18}$/.test(cleanAcc)) {
        return setError('Bank account number must be between 9 and 18 digits (numbers only).');
      }
      const cleanIfsc = form.ifscCode.trim();
      if (!/^[A-Za-z0-9]{6,14}$/.test(cleanIfsc)) {
        return setError('IFSC code must be between 6 and 14 alphanumeric characters (no special characters).');
      }
    }

    setSaving(true);
    try {
      await axios.post(`${API}/wallet/bank-details`,
        { 
          accountHolderName: form.accountHolderName.trim(),
          upiId: form.upiId ? form.upiId.trim() : null,
          accountNumber: form.accountNumber ? form.accountNumber.trim() : null,
          ifscCode: form.ifscCode ? form.ifscCode.trim().toUpperCase() : null,
          bankName: form.bankName ? form.bankName.trim() : null,
          accountType: form.accountType || 'savings'
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess('Bank details saved successfully!');
      setEditing(false);
      fetchBankDetails();
      onSaved?.();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.msg || err.response?.data?.detail || 'Failed to save bank details.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '1rem', color: '#374151', fontWeight: '600', fontSize: '0.88rem' }}>
      Loading bank details...
    </div>
  );

  const hasDetails = !!existing;

  return (
    <div style={{
      background: 'white',
      borderRadius: compact ? '16px' : '20px',
      border: `1.5px solid ${hasDetails ? '#86efac' : required ? '#FCD34D' : '#EEF2E6'}`,
      padding: compact ? '1.25rem' : '1.75rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: hasDetails ? '#D1FAE5' : '#FEF3C7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Building2 size={18} color={hasDetails ? '#16a34a' : '#D97706'} />
          </div>
          <div>
            <h3 style={{ fontWeight: '800', fontSize: '1rem', color: '#161616', margin: 0 }}>
              Bank Details
              {required && !hasDetails && (
                <span style={{ marginLeft: '8px', background: '#FEF3C7', color: '#D97706', fontSize: '0.65rem', fontWeight: '800', padding: '2px 8px', borderRadius: '6px' }}>
                  REQUIRED
                </span>
              )}
            </h3>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#374151', fontWeight: '500' }}>
              {hasDetails ? 'Your bank account for payouts & withdrawals' : 'Add your bank account to enable payments & withdrawals'}
            </p>
          </div>
        </div>

        {hasDetails && !editing && (
          <button
            onClick={() => setEditing(true)}
            style={{ background: 'none', border: '1.5px solid #EEF2E6', borderRadius: '8px', padding: '6px 14px', fontWeight: '700', fontSize: '0.8rem', color: '#084734', cursor: 'pointer' }}
          >
            ✏️ Edit
          </button>
        )}
      </div>

      {/* Required banner */}
      {required && !hasDetails && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#FEF3C7', border: '1.5px solid #FCD34D',
          borderRadius: '10px', padding: '10px 14px', marginBottom: '1.25rem',
        }}>
          <AlertCircle size={16} color="#D97706" />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#92400E' }}>
            Bank details are required to make bookings and process refunds/payouts.
          </span>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#D1FAE5', border: '1.5px solid #86efac', borderRadius: '10px', padding: '10px 14px', marginBottom: '1.25rem' }}>
          <CheckCircle size={16} color="#16a34a" />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#065F46' }}>{success}</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{ background: '#FEE2E2', border: '1.5px solid #FECACA', borderRadius: '10px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '0.82rem', fontWeight: '700', color: '#DC2626' }}>
          {error}
        </div>
      )}

      {/* View mode */}
      {hasDetails && !editing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <BankField label="Account Holder" value={existing.accountHolderName} />
          {existing.upiId && <BankField label="UPI ID" value={existing.upiId} valueColor="#084734" />}
          {existing.accountNumber && (
            <BankField
              label="Account Number"
              value={showAccount ? existing.accountNumber : existing.maskedAccountNumber}
              action={
                <button onClick={() => setShowAccount(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex' }}>
                  {showAccount ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              }
            />
          )}
          {existing.ifscCode && <BankField label="IFSC Code" value={existing.ifscCode} />}
          {existing.bankName && <BankField label="Bank Name" value={existing.bankName} />}
          {existing.accountNumber && <BankField label="Account Type" value={(existing.accountType || 'savings').charAt(0).toUpperCase() + (existing.accountType || 'savings').slice(1)} />}
          <BankField
            label="Status"
            value={existing.isVerified ? '✓ Verified' : 'Saved'}
            valueColor={existing.isVerified ? '#16a34a' : '#374151'}
          />
        </div>
      )}

      {/* Edit/Add form */}
      {editing && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <FormField label="Account Holder Name *">
              <input
                type="text"
                placeholder="As per bank records"
                value={form.accountHolderName}
                onChange={e => { setForm({ ...form, accountHolderName: e.target.value }); setError(''); }}
                style={inpStyle}
              />
            </FormField>

            <FormField label="UPI ID (For Instant Payouts)">
              <input
                type="text"
                placeholder="e.g. name@bank"
                value={form.upiId}
                onChange={e => { setForm({ ...form, upiId: e.target.value }); setError(''); }}
                style={inpStyle}
              />
            </FormField>

            <FormField label="Account Number">
              <input
                type="text"
                placeholder="Enter account number"
                value={form.accountNumber}
                onChange={e => { setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, '') }); setError(''); }}
                style={inpStyle}
                maxLength={18}
              />
            </FormField>

            <FormField label="IFSC Code">
              <input
                type="text"
                placeholder="e.g. SBIN0001234"
                value={form.ifscCode}
                onChange={e => { setForm({ ...form, ifscCode: e.target.value.toUpperCase() }); setError(''); }}
                style={inpStyle}
                maxLength={14}
              />
            </FormField>

            <FormField label="Bank Name">
              <input
                type="text"
                placeholder="e.g. State Bank of India"
                value={form.bankName}
                onChange={e => setForm({ ...form, bankName: e.target.value })}
                style={inpStyle}
              />
            </FormField>

            <FormField label="Account Type">
              <select
                value={form.accountType}
                onChange={e => setForm({ ...form, accountType: e.target.value })}
                style={inpStyle}
              >
                <option value="savings">Savings Account</option>
                <option value="current">Current Account</option>
              </select>
            </FormField>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: '#084734', color: '#CEF17B', border: 'none',
                padding: '11px 24px', borderRadius: '10px', fontWeight: '800',
                fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.65 : 1,
              }}
            >
              {saving ? 'Saving...' : hasDetails ? '💾 Update Details' : '💾 Save Bank Details'}
            </button>
            {hasDetails && (
              <button
                onClick={() => { setEditing(false); setError(''); }}
                style={{ background: 'none', border: '1.5px solid #EEF2E6', borderRadius: '10px', padding: '11px 20px', fontWeight: '700', fontSize: '0.88rem', color: '#374151', cursor: 'pointer' }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BankField({ label, value, valueColor, action }) {
  return (
    <div style={{ background: '#F8FAF7', borderRadius: '10px', padding: '10px 14px', border: '1px solid #EEF2E6' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.88rem', fontWeight: '700', color: valueColor || '#161616' }}>{value}</span>
        {action}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inpStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '10px',
  border: '1.5px solid #EEF2E6', fontSize: '0.9rem', fontWeight: '600',
  color: '#161616', background: 'white', outline: 'none', boxSizing: 'border-box',
};
